import { useCallback } from 'react';
import { useLocale } from '@hooks/useLocale';
import { Address, AddressField } from '@adapters/bigcommerce/types/address';
import useAddresses from './useAddresses';
import { HookError } from '../../errorHandlers';

export const updateAddressFetcher = async (
  address: Address,
  locale: string
): Promise<Address> => {
  if (!address) {
    throw new HookError('Body must be provided to update the address');
  }
  const updateAddressResponse = await fetch('/api/customer/updateaddress', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
    },
    body: JSON.stringify(address),
  });

  if (updateAddressResponse.status !== 200) {
    throw new HookError('Add address request failed');
  }

  const { data } = await updateAddressResponse.json();
  return data ?? [];
};

const useUpdateAddress = () => {
  const { data = [], mutate } = useAddresses();
  const locale = useLocale();
  if (!locale) throw new HookError('Locale must be specified to login');

  return useCallback(
    async (address: Address) => {
      const updatedAddress = await updateAddressFetcher(address, locale);

      const newAddresses = data.map((currentAddress) => {
        if (
          currentAddress[AddressField.id] === updatedAddress[AddressField.id]
        ) {
          return updatedAddress;
        }

        return currentAddress;
      });
      mutate(newAddresses);
      return updatedAddress;
    },
    [mutate, data, locale]
  );
};

export default useUpdateAddress;
